import type {
  DimenzijaZaPrikaz,
  LetoPodatkov,
  NacinMatrike,
  NastavitvePartnerjev,
  OdgovorPorocila,
  PorociloNaVoljo,
  Porocilo as PorociloTip,
  StranPrometa,
  VrsticaPorocila,
  VrstaObdobjaObcutljivosti,
  VrstaPorocila,
} from '@koda/shared';
import { POROCILA_Z_RAZPONOM } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { api, dodajParametrePartnerjev, type ParametriPartnerjev } from '../lib/api';
import { zavihekIzvozaVseh } from '../lib/porocila';
import { useBesedila, useJezik } from '../lib/jezik';
import { GlavaKazalnikov, PlosciceKazalnikov } from '../sestavni/GlavaKazalnikov';
import { FilterPartnerjev } from '../sestavni/FilterPartnerjev';
import { IzbirnikDimenzije, type IzbiraDimenzije } from '../sestavni/IzbirnikDimenzije';
import { IzbirnikMesecev } from '../sestavni/IzbirnikMesecev';
import { obdobjeLeta } from '../sestavni/kazalniki-skupno';
import { ObcutljivostKazalnikov } from '../sestavni/ObcutljivostKazalnikov';
import { PanelRazclenitve, type Celica } from '../sestavni/PanelRazclenitve';
import { PodrobnoKazalnikov } from '../sestavni/PodrobnoKazalnikov';
import { PregledKazalnikov } from '../sestavni/PregledKazalnikov';
import { TabelaPartnerjev } from '../sestavni/TabelaPartnerjev';
import { TabelaPorocila } from '../sestavni/TabelaPorocila';
import { TabelaPrimerjave } from '../sestavni/TabelaPrimerjave';

const VRSTE: VrstaPorocila[] = ['hitri', 'rezultat', 'bs-s', 'cf', 'bs', 'primerjava', 'kazalniki', 'partnerji', 'rvc-kupci'];

/** Poročila, ki primerjajo dve leti in imajo zato izbiro primerjalnega leta. */
const Z_PRIMERJALNIM: VrstaPorocila[] = ['primerjava', 'kazalniki', 'partnerji', 'rvc-kupci'];

/** Zavihka po partnerjih (docs/ARHITEKTURA.md, 6.5): svoja tabela in izbira kontov. */
const PO_PARTNERJIH: VrstaPorocila[] = ['partnerji', 'rvc-kupci'];

/** Koliko ravni ima tabela. */
function globinaTabele(vrstice: VrsticaPorocila[]): number {
  return vrstice.reduce((najvec, v) => Math.max(najvec, 1 + globinaTabele(v.otroci)), 0);
}

/**
 * Privzeti pogled: skupina in prva raven clenitve, nic globlje.
 *
 * Vse pod tem so podrobni konti - v Rezultatu stirimestni, v bilanci stanja
 * tri- in stirimestni, v denarnem toku konti pod postavko. Vsi so za stikalom,
 * sicer gumb pri bilanci in denarnem toku ne bi spremenil nicesar.
 */
const PRIVZETE_RAVNI = 2;

/** Stran s poročilom in razčlenitvijo. */
export function Porocilo(): JSX.Element {
  const b = useBesedila();
  const jezik = useJezik();
  const { id = '', vrsta = 'hitri' } = useParams();
  const [parametri, nastaviParametre] = useSearchParams();
  const leto = Number.parseInt(parametri.get('leto') ?? '', 10);

  /**
   * Izbrana dimenzija je v naslovu strani, ne v stanju komponente.
   *
   * Tako ostane ob preklopu med poročili in letom, stran pa je deljiva:
   * naslov opisuje točno tisto, kar uporabnik vidi.
   */
  const kodaDimenzije = parametri.get('dimenzija') ?? '';
  const brezDimenzije = parametri.get('brezDimenzije') === 'true';
  // Vrednosti je lahko vec; stara oblika `vrednost=` se se prebere.
  const vrednostiDimenzije = [
    ...parametri.getAll('vrednosti'),
    ...parametri.getAll('vrednost'),
  ].filter((v) => v !== '');
  const izbranaDimenzija: IzbiraDimenzije | null =
    kodaDimenzije === ''
      ? null
      : { koda: kodaDimenzije, vrednosti: vrednostiDimenzije, brez: brezDimenzije };
  const kljucDimenzije = `${kodaDimenzije}|${vrednostiDimenzije.join(',')}|${String(brezDimenzije)}`;

  /**
   * Primerjalno leto pri Primerjavi; tako kot dimenzija je v naslovu strani.
   *
   * Privzeto je najnovejše starejše leto s knjižbami, sicer prejšnje leto
   * (docs/ARHITEKTURA.md, 6.1). Pri drugih poročilih se ne uporablja.
   *
   * `null` pomeni, da se leta še berejo; prazen seznam pomeni, da jih ni.
   */
  const [leta, setLeta] = useState<LetoPodatkov[] | null>(null);
  const starejsaLeta = (leta ?? []).map((l) => l.leto).filter((l) => l < leto);
  const izbranoPrimerjalno = Number.parseInt(parametri.get('primerjalno') ?? '', 10);
  const primerjalno = Number.isFinite(izbranoPrimerjalno)
    ? izbranoPrimerjalno
    : (starejsaLeta[0] ?? leto - 1);
  /**
   * Primerjalno leto za zahtevo. Kadar starejšega leta s knjižbami ni (prvo
   * uvoženo leto podjetja), se ne pošlje: strežnik takrat vrne poročilo brez
   * primerjave. Poslano `leto − 1` bi strežnik pravilno zavrnil, ker tistega
   * leta ni, in Kazalniki ne bi pokazali ničesar, tudi tega, kar primerjave
   * ne potrebuje.
   */
  const primerjalnoZaZahtevo = Number.isFinite(izbranoPrimerjalno) ? izbranoPrimerjalno : starejsaLeta[0];

  /**
   * Ali je treba pred zahtevo počakati na seznam let.
   *
   * Privzeto primerjalno leto je zadnje starejše leto s knjižbami, zato ga
   * brez seznama ni mogoče določiti. Če bi vmes poslali zahtevo z `leto − 1`
   * in tistega leta ni, bi strežnik pravilno vrnil 400 — odgovor pa bi lahko
   * prišel **za** pravim poročilom in na zaslonu bi ostala napaka, ki ne
   * drži več. Kadar je primerjalno leto v naslovu, čakati ni treba, ker ni
   * odvisno od seznama; pri drugih poročilih pa se sploh ne uporablja.
   */
  const jeZPrimerjalnim = Z_PRIMERJALNIM.includes(vrsta as VrstaPorocila);
  const cakamNaLeta = jeZPrimerjalnim && leta === null && !Number.isFinite(izbranoPrimerjalno);

  /**
   * Leto poročila je izbirno tudi v poročilu samem, ne samo na strani
   * podjetja: analitik lahko preklaplja med leti, ne da bi zapustil zavihek.
   * Ponujena so vsa leta s knjižbami (tudi starejša so tako ali tako
   * dosegljiva prek primerjalnega leta, 6.1). Dimenzija, razpon mesecev in
   * izbira zavihka ostanejo; primerjalno leto, ki ni več starejše od
   * izbranega, se odstrani in velja privzeto (zadnje starejše leto).
   */
  const vsaLeta = (leta ?? []).map((l) => l.leto);
  const ponujenaLeta = Number.isFinite(leto) && !vsaLeta.includes(leto) ? [leto, ...vsaLeta] : vsaLeta;
  const izberiLeto = (novo: number): void => {
    const novi = new URLSearchParams(parametri);
    novi.set('leto', String(novo));
    const izbranoPrimerjalnoLeto = Number.parseInt(novi.get('primerjalno') ?? '', 10);
    if (Number.isFinite(izbranoPrimerjalnoLeto) && izbranoPrimerjalnoLeto >= novo) novi.delete('primerjalno');
    nastaviParametre(novi);
  };
  /**
   * Izbira leta za Kazalnike: gumb v glavi, enak izbiri primerjalnega leta.
   * Pri drugih poročilih je izbira leto v naslovu (letoVNaslovu).
   */
  const izbiraLeta = (razred: string, naziv: JSX.Element | string): JSX.Element | null =>
    ponujenaLeta.length > 1 ? (
      <label className={razred}>
        {naziv}
        <select
          aria-label={b.letoPorocila}
          value={String(leto)}
          onChange={(d) => {
            izberiLeto(Number.parseInt(d.target.value, 10));
          }}
        >
          {ponujenaLeta.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>
    ) : null;
  /**
   * Leto v naslovu poročila (»Hitri pregled 2025«) je hkrati izbira: klik na
   * letnico odpre seznam let. Brez drugih let je samo besedilo.
   */
  const letoVNaslovu = (prikazano: number): JSX.Element =>
    ponujenaLeta.length > 1 ? (
      <select
        className="leto-oznaka leto-v-naslovu"
        aria-label={b.letoPorocila}
        title={b.zamenjajLeto}
        value={String(leto)}
        onChange={(d) => {
          izberiLeto(Number.parseInt(d.target.value, 10));
        }}
      >
        {ponujenaLeta.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>
    ) : (
      <span className="leto-oznaka">{prikazano}</span>
    );

  const izberiPrimerjalno = (izbrano: number): void => {
    const novi = new URLSearchParams(parametri);
    novi.set('primerjalno', String(izbrano));
    nastaviParametre(novi);
  };

  /**
   * Razpon mesecev pri Primerjavi, prav tako v naslovu strani.
   *
   * Brez njega velja privzeti razpon - meseci, ki jih ima tekoče leto - in ga
   * določi strežnik; stran ga prebere iz stolpcev poročila.
   */
  const obdobjeOd = Number.parseInt(parametri.get('obdobjeOd') ?? '', 10);
  const obdobjeDo = Number.parseInt(parametri.get('obdobjeDo') ?? '', 10);
  const obdobje =
    Number.isFinite(obdobjeOd) && Number.isFinite(obdobjeDo)
      ? { od: obdobjeOd, do: obdobjeDo }
      : undefined;
  const kljucObdobja = obdobje === undefined ? '' : `${String(obdobje.od)}-${String(obdobje.do)}`;
  /** Razpon mesecev v naslovu strani; `undefined` ga odstrani (vsi meseci). */
  const izberiObdobje = (razpon: { od: number; do: number } | undefined): void => {
    const novi = new URLSearchParams(parametri);
    if (razpon === undefined) {
      novi.delete('obdobjeOd');
      novi.delete('obdobjeDo');
    } else {
      novi.set('obdobjeOd', String(Math.min(razpon.od, razpon.do)));
      novi.set('obdobjeDo', String(Math.max(razpon.od, razpon.do)));
    }
    nastaviParametre(novi);
  };
  /**
   * Razpon mesecev velja pri Primerjavi in mesečnih izpisih (Hitri pregled,
   * Rezultat, obe bilanci). V naslovu strani je en sam, zato ostane ob
   * preklopu med njimi (docs/ARHITEKTURA.md, 6.1).
   */
  const zRazponom = POROCILA_Z_RAZPONOM.includes(vrsta as VrstaPorocila);
  const obdobjeZahteve = zRazponom ? obdobje : undefined;

  /**
   * Partnerji in RVC po kupcih: izbrani konti, stran in število vrstic so
   * v naslovu strani kot vse druge izbire. Brez kontov veljajo privzete
   * prednastavitve podjetja (docs/ARHITEKTURA.md, 6.5).
   */
  const jePoPartnerjih = PO_PARTNERJIH.includes(vrsta as VrstaPorocila);
  const predpone = parametri.getAll('predpone').filter((k) => k !== '');
  const stranParametra = parametri.get('stran');
  const stran: StranPrometa | undefined = stranParametra === 'breme' || stranParametra === 'dobro' ? stranParametra : undefined;
  const steviloParametra = Number.parseInt(parametri.get('n') ?? '', 10);
  const izbiraPartnerjev: ParametriPartnerjev = {
    ...(predpone.length > 0 ? { predpone } : {}),
    ...(stran === undefined ? {} : { stran }),
    ...(Number.isFinite(steviloParametra) ? { n: steviloParametra } : {}),
    ...(parametri.get('vrstni') === 'najslabsi' ? { vrstni: 'najslabsi' as const } : {}),
  };
  const parametriPartnerjev = jePoPartnerjih ? izbiraPartnerjev : undefined;
  const kljucPartnerjev = jePoPartnerjih ? `${predpone.join(',')}|${stran ?? ''}|${String(izbiraPartnerjev.n ?? '')}|${izbiraPartnerjev.vrstni ?? ''}` : '';
  const izberiPartnerje = (nova: ParametriPartnerjev): void => {
    const novi = new URLSearchParams(parametri);
    novi.delete('predpone');
    novi.delete('stran');
    novi.delete('n');
    novi.delete('vrstni');
    dodajParametrePartnerjev(novi, nova);
    nastaviParametre(novi);
  };
  const [nastavitvePartnerjev, setNastavitvePartnerjev] = useState<NastavitvePartnerjev | null>(null);
  useEffect(() => {
    if (!jePoPartnerjih) return;
    let prekinjeno = false;
    api
      .nastavitvePartnerjev(id)
      .then((n) => {
        if (!prekinjeno) setNastavitvePartnerjev(n);
      })
      .catch(() => {
        // Brez nastavitev ostane samo izbira po meri; poročilo se vseeno pokaže.
        if (!prekinjeno) setNastavitvePartnerjev(null);
      });
    return () => {
      prekinjeno = true;
    };
  }, [id, jePoPartnerjih, jezik]);

  /**
   * Kazalniki: Pregled (privzeto, nadzorna plosca) ali Podrobno (tabele).
   * Izbira je v naslovu strani, da ostane ob menjavi leta in dimenzije.
   */
  const podrobno = parametri.get('pogled') === 'podrobno';
  const obcutljivost = vrsta === 'kazalniki' && parametri.get('pogled') === 'obcutljivost';
  const izberiPogled = (nov: 'pregled' | 'podrobno' | 'obcutljivost'): void => {
    const novi = new URLSearchParams(parametri);
    if (nov === 'pregled') novi.delete('pogled');
    else novi.set('pogled', nov);
    nastaviParametre(novi);
  };

  /**
   * Zavihek Občutljivost: obdobje, strošek in način matrike so v naslovu
   * strani, da ostanejo ob osvežitvi in se stran da deliti. Strošek se ob
   * menjavi obdobja pozabi – v drugem obdobju ga morda ni.
   */
  const obdobjeAnalize = ((): VrstaObdobjaObcutljivosti | undefined => {
    const v = parametri.get('obdobjeAnalize');
    return v === 'celo' || v === 'tekoce' ? v : undefined;
  })();
  const strosek = parametri.get('strosek') ?? undefined;
  const nacin: NacinMatrike = parametri.get('nacin') === 'cena' ? 'cena' : 'kolicina';
  const nastaviParameter = (kljuc: string, vrednost: string | null, pozabi: string[] = []): void => {
    const novi = new URLSearchParams(parametri);
    for (const k of pozabi) novi.delete(k);
    if (vrednost === null) novi.delete(kljuc);
    else novi.set(kljuc, vrednost);
    nastaviParametre(novi);
  };

  /** Stikalo Pregled · Podrobno · Občutljivost v glavi Kazalnikov. */
  const stikaloPogleda = (
    <div className="stikalo-pogleda" role="group" aria-label={b.pk.kazalniki}>
      <button
        type="button"
        aria-pressed={!podrobno && !obcutljivost}
        onClick={() => {
          izberiPogled('pregled');
        }}
      >
        {b.pk.pregled}
      </button>
      <button
        type="button"
        aria-pressed={podrobno}
        onClick={() => {
          izberiPogled('podrobno');
        }}
      >
        {b.pk.podrobno}
      </button>
      <button
        type="button"
        aria-pressed={obcutljivost}
        onClick={() => {
          izberiPogled('obcutljivost');
        }}
      >
        {b.ob.zavihek}
      </button>
    </div>
  );

  const [dimenzije, setDimenzije] = useState<DimenzijaZaPrikaz[]>([]);
  // Izvoz v Excel je svoj modul; gumba ne kazemo, ce ga podjetje nima.
  const [moduli, setModuli] = useState<string[]>([]);

  const izberiDimenzijo = (izbira: IzbiraDimenzije | null): void => {
    const novi = new URLSearchParams(parametri);
    novi.delete('dimenzija');
    novi.delete('vrednost');
    novi.delete('vrednosti');
    novi.delete('brezDimenzije');
    if (izbira !== null) {
      novi.set('dimenzija', izbira.koda);
      for (const v of izbira.vrednosti) novi.append('vrednosti', v);
      if (izbira.brez) novi.set('brezDimenzije', 'true');
    }
    nastaviParametre(novi);
  };

  const [odgovor, setOdgovor] = useState<OdgovorPorocila | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [celica, setCelica] = useState<Celica | null>(null);
  const [podrobniKonti, setPodrobniKonti] = useState(false);
  // Katera porocila podjetje ima, pove paket (zavihki so v glavnem meniju,
  // tu je seznam za izvoz vseh porocil).
  const [naVoljo, setNaVoljo] = useState<PorociloNaVoljo[]>([]);

  // Katera leta ima podjetje uvozena, pove izbirnik primerjalnega leta.
  useEffect(() => {
    let prekinjeno = false;
    api
      .leta(id)
      .then((l) => {
        if (!prekinjeno) setLeta(l);
      })
      .catch(() => {
        // Brez seznama ostane privzeto prejsnje leto; porocilo naj se vseeno
        // poskusi prikazati, sicer bi ena spodletela zahteva ustavila stran.
        if (!prekinjeno) setLeta([]);
      });
    return () => {
      prekinjeno = true;
    };
  }, [id]);

  useEffect(() => {
    api
      .porocilaNaVoljo(id, jezik)
      .then(setNaVoljo)
      .catch(() => {
        setNaVoljo([]);
      });
  }, [id, jezik]);

  useEffect(() => {
    if (!VRSTE.includes(vrsta as VrstaPorocila) || !Number.isFinite(leto)) {
      setNapaka(b.napacenNaslov);
      return;
    }
    setOdgovor(null);
    setCelica(null);
    setNapaka(null);
    if (cakamNaLeta) return;
    // Občutljivost bere svoje podatke; poročila Kazalnikov ne potrebuje.
    if (obcutljivost) return;

    // Odgovor zahteve, ki ni vec aktualna, se zavrze: sicer bi lahko pozna
    // napaka povozila ze prikazano porocilo.
    let prekinjeno = false;
    api
      .porocilo(
        id,
        vrsta as VrstaPorocila,
        leto,
        izbranaDimenzija ?? undefined,
        jezik,
        primerjalnoZaZahtevo,
        obdobjeZahteve,
        parametriPartnerjev,
      )
      .then((o) => {
        if (!prekinjeno) setOdgovor(o);
      })
      .catch((e: unknown) => {
        if (!prekinjeno) setNapaka(e instanceof Error ? e.message : b.porociloNiMogoce);
      });
    return () => {
      prekinjeno = true;
    };
  }, [id, vrsta, leto, kljucDimenzije, jezik, primerjalnoZaZahtevo, cakamNaLeta, kljucObdobja, obcutljivost, kljucPartnerjev]);

  // Kaj je sploh na voljo, povedo uvozene knjizbe tega leta. Stevila se
  // nanasajo na odprto porocilo, zato se seznam osvezi tudi ob menjavi zavihka.
  useEffect(() => {
    if (!Number.isFinite(leto) || !VRSTE.includes(vrsta as VrstaPorocila)) return;
    if (cakamNaLeta) return;
    let prekinjeno = false;
    api
      .dimenzije(
        id,
        leto,
        vrsta as VrstaPorocila,
        jezik,
        primerjalnoZaZahtevo,
        obdobjeZahteve,
      )
      .then((d) => {
        if (!prekinjeno) setDimenzije(d);
      })
      .catch(() => {
        if (!prekinjeno) setDimenzije([]);
      });
    return () => {
      prekinjeno = true;
    };
  }, [id, leto, vrsta, jezik, primerjalnoZaZahtevo, cakamNaLeta, kljucObdobja]);

  useEffect(() => {
    api
      .moduli(id)
      .then(setModuli)
      .catch(() => {
        setModuli([]);
      });
  }, [id]);

  // Hitri pregled se po kontih sploh ne drobi, zato tam stikala ni.
  const jePodrobnejsi =
    vrsta === 'rezultat' || vrsta === 'bs-s' || vrsta === 'cf' || vrsta === 'bs';
  const globinaZa = (tabela: PorociloTip): number =>
    jePodrobnejsi && !podrobniKonti ? PRIVZETE_RAVNI : globinaTabele(tabela.vrstice);

  /**
   * Naslov za izvoz: isti filter kot prikaz, zato se številki ne razideta.
   *
   * `vsa` izvozi vsa vklopljena poročila v eno datoteko, vsako v svoj zavihek.
   */
  const povezavaIzvoza = (vsa = false): string => {
    const q = new URLSearchParams({ leto: String(leto) });
    if (izbranaDimenzija !== null) {
      q.set('dimenzija', izbranaDimenzija.koda);
      for (const v of izbranaDimenzija.vrednosti) q.append('vrednosti', v);
      if (izbranaDimenzija.brez) q.set('brezDimenzije', 'true');
    }
    // Datoteka naj ima iste vrstice kot zaslon: podrobni konti so za stikalom.
    if (jePodrobnejsi && !podrobniKonti) q.set('ravni', String(PRIVZETE_RAVNI));
    // Datoteka je v istem jeziku kot zaslon.
    q.set('jezik', jezik);
    // Primerjava in Kazalniki morata imeti isto primerjalno leto kot zaslon,
    // Primerjava tudi isti razpon.
    if (jeZPrimerjalnim && primerjalnoZaZahtevo !== undefined) q.set('primerjalno', String(primerjalnoZaZahtevo));
    // Isti razpon mesecev; pri izvozu vseh poročil velja za vsa, ki ga imajo.
    if ((zRazponom || vsa) && obdobje !== undefined) {
      q.set('obdobjeOd', String(obdobje.od));
      q.set('obdobjeDo', String(obdobje.do));
    }
    // Partnerji: isti konti, stran in število vrstic kot na zaslonu.
    if (parametriPartnerjev !== undefined && !vsa) dodajParametrePartnerjev(q, parametriPartnerjev);
    return vsa
      ? `/api/podjetja/${id}/porocila/izvoz?${q.toString()}`
      : `/api/podjetja/${id}/porocila/${vrsta}/izvoz?${q.toString()}`;
  };


  /**
   * Podnaslov glave Kazalnikov: obdobje, primerjalno leto in stanje knjižb na
   * zadnji dan meseca n (»Januar–julij 2026, primerjalno leto 2025 · stanje
   * knjižb 31. 7. 2026«). Nevtralno »primerjalno leto«, ne »enako obdobje«:
   * primerjalno leto je lahko uvoženo krajše od meseca n (5.1), kar pove
   * opozorilo pod glavo.
   */
  const podnaslovKazalnikov = (o: OdgovorPorocila): string => {
    const p = o.pregled;
    if (p === undefined || p.n === 0) return String(o.leto);
    const dan = new Date(Date.UTC(p.leto, p.n, 0)).getUTCDate();
    return b.pk.glavaPodnaslov(
      obdobjeLeta(p.n, p.leto, b.meseci),
      p.imaPrimerjalno ? p.primerjalno : null,
      `${String(dan)}. ${String(p.n)}. ${String(p.leto)}`,
    );
  };

  return (
    <main className="siroko">
      <div className="vsebina">
        {/* Zavihki poročil in izbira podjetja so v glavnem meniju (Glava). */}
        {/*
          Izbira leta ostane tudi med nalaganjem in ob napaki: menjava leta
          počisti poročilo, in če nova zahteva odpove, mora biti mogoče
          izbrati drugo leto (npr. prejšnje). Ko je poročilo naloženo, je
          izbira leto v naslovu oziroma v glavi Kazalnikov; dokler naslova ni,
          je sama letnica.
        */}
        {odgovor === null && !obcutljivost && ponujenaLeta.length > 1 && <h1>{letoVNaslovu(leto)}</h1>}
        {napaka !== null && <p className="napaka">{napaka}</p>}
        {odgovor === null && napaka === null && !obcutljivost && <p>{b.racunamPorocilo}</p>}

        {obcutljivost && (
          <ObcutljivostKazalnikov
            podjetje={id}
            naslov={b.pk.kazalniki}
            stikaloPogleda={stikaloPogleda}
            obdobje={obdobjeAnalize}
            naObdobje={(o) => {
              nastaviParameter('obdobjeAnalize', o, ['strosek']);
            }}
            strosek={strosek}
            naStrosek={(k) => {
              nastaviParameter('strosek', k);
            }}
            nacin={nacin}
            naNacin={(n) => {
              nastaviParameter('nacin', n === 'cena' ? 'cena' : null);
            }}
          />
        )}

        {odgovor !== null && vrsta === 'kazalniki' && (
          <div className="kz-stran">
            <GlavaKazalnikov naslov={odgovor.naslov} podnaslov={podnaslovKazalnikov(odgovor)}>
              {izbiraLeta('kz-kontrola', <span className="kz-kontrola-naziv">{b.letoPorocila}</span>)}
              <IzbirnikDimenzije dimenzije={dimenzije} izbrana={izbranaDimenzija} naIzbiro={izberiDimenzijo} />
              {starejsaLeta.length > 0 && (
                <label className="kz-kontrola">
                  <span className="kz-kontrola-naziv">{b.pk.primerjava}</span>
                  <select
                    value={String(primerjalno)}
                    onChange={(d) => {
                      izberiPrimerjalno(Number.parseInt(d.target.value, 10));
                    }}
                  >
                    {(starejsaLeta.includes(primerjalno) ? starejsaLeta : [primerjalno, ...starejsaLeta]).map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {moduli.includes('izvoz-excel') && (
                <a className="kz-gumb" href={povezavaIzvoza()}>
                  {b.pk.izvozVExcel}
                </a>
              )}
              {odgovor.pregled !== undefined && stikaloPogleda}
            </GlavaKazalnikov>

            {odgovor.opozorila.map((o) => (
              <p key={o} className="opozorilo">
                {o}
              </p>
            ))}

            {odgovor.pregled !== undefined && (
              <PlosciceKazalnikov ploscice={odgovor.pregled.ploscice} leto={odgovor.pregled.leto} naCelico={setCelica} />
            )}

            {odgovor.pregled !== undefined && !podrobno ? (
              <PregledKazalnikov
                pregled={odgovor.pregled}
                naCelico={setCelica}
                naPodrobno={() => {
                  izberiPogled('podrobno');
                }}
              />
            ) : odgovor.pregled !== undefined ? (
              <PodrobnoKazalnikov tabele={odgovor.tabele} pregled={odgovor.pregled} naCelico={setCelica} />
            ) : (
              <TabelaPrimerjave tabele={odgovor.tabele} naCelico={setCelica} naslovVrstic={b.kazalnik} />
            )}
          </div>
        )}

        {odgovor !== null && vrsta !== 'kazalniki' && (
          <>
            <h1>
              {odgovor.naslov} {letoVNaslovu(odgovor.leto)}
            </h1>
            <p className="namig">{jePoPartnerjih ? b.pa.namig : b.namigPorocila}</p>

            {/*
              Opozorila celotnega poročila (npr. neizravnan zadnji uvoz) so
              enkrat nad vsemi tabelami: uvoz je en sam, zato bi jih Hitri
              pregled z dvema tabelama sicer pokazal dvakrat.
            */}
            {odgovor.opozorila.map((o) => (
              <p key={o} className="opozorilo">
                {o}
              </p>
            ))}

            {/*
              Dimenzija in razpon mesecev v isti vrsti: oba zožita celotno
              poročilo. Razpon »od–do« je pri Primerjavi in mesečnih izpisih
              (docs/ARHITEKTURA.md, 6.1).
            */}
            <div className="filtri-porocila">
              <IzbirnikDimenzije
                dimenzije={dimenzije}
                izbrana={izbranaDimenzija}
                naIzbiro={izberiDimenzijo}
              />
              {/*
                Meni mesecev pod dimenzijo, enak meniju »Vrednost«. Ostane
                tudi, ko za izbrani razpon ni nobene tabele (razpon za
                pokritostjo uvoza) – sicer izbire ne bi bilo mogoče spremeniti.
              */}
              {zRazponom && (
                <IzbirnikMesecev izbran={obdobje} pokritost={odgovor.pokritost} naIzbiro={izberiObdobje} />
              )}
              {jePoPartnerjih && (
                <FilterPartnerjev
                  podjetje={id}
                  vrsta={vrsta === 'partnerji' ? 'partnerji' : 'rvc-kupci'}
                  nastavitve={nastavitvePartnerjev}
                  izbira={izbiraPartnerjev}
                  naIzbiro={izberiPartnerje}
                />
              )}
            </div>

            {/*
              Primerjalno leto imajo Primerjava, Kazalniki, Partnerji in RVC po
              kupcih (Z_PRIMERJALNIM). Privzeto je zadnje starejše leto s
              knjižbami, zamenjati ga je mogoče s katerim koli starejšim
              (docs/ARHITEKTURA.md, 6.1). Če starejših let ni, izbire ni in
              primerjalnega leta tudi ni.
            */}

            {jeZPrimerjalnim && starejsaLeta.length > 0 && (
              <label className="drobno primerjalno">
                {b.primerjalnoLeto}
                <select
                  value={String(primerjalno)}
                  onChange={(d) => {
                    izberiPrimerjalno(Number.parseInt(d.target.value, 10));
                  }}
                >
                  {(starejsaLeta.includes(primerjalno)
                    ? starejsaLeta
                    : [primerjalno, ...starejsaLeta]
                  ).map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {moduli.includes('izvoz-excel') && (
              <p className="izvoz-porocila">
                <a className="povezava" href={povezavaIzvoza()}>
                  {b.izvoziPorocilo}
                </a>
                {/*
                  Izvoz vseh poročil je samo na prvem zavihku s tabelami (Hitri
                  pregled je osnovni pogled stranke; Kazalniki, ki so lahko
                  prvi, tabel nimajo). Če bi bil na vsakem, bi bila nad vsako
                  tabelo dva gumba in preveč šare.
                */}
                {naVoljo.length > 1 && vrsta === zavihekIzvozaVseh(naVoljo) && (
                  <>
                    {' · '}
                    <a className="povezava" href={povezavaIzvoza(true)}>
                      {b.izvoziVsa}
                    </a>
                  </>
                )}
              </p>
            )}

            {jePodrobnejsi && (
              <label className="stikalo">
                <input
                  type="checkbox"
                  checked={podrobniKonti}
                  onChange={(e) => {
                    setPodrobniKonti(e.target.checked);
                  }}
                />
                {b.pokaziPodrobneKonte}
              </label>
            )}

            {vrsta === 'primerjava' ? (
              <TabelaPrimerjave tabele={odgovor.tabele} naCelico={setCelica} izbrana={celica?.vrstica ?? null} />
            ) : jePoPartnerjih ? (
              odgovor.tabele.length === 0 ? (
                <p className="drobno">{b.pa.brezTabel}</p>
              ) : (
                odgovor.tabele.map((tabela) => (
                  <section key={tabela.naslov} className="tabela-odsek">
                    <h2>{tabela.naslov}</h2>
                    <p className="podnaslov">{tabela.pojasnilo}</p>
                    {tabela.opozorila.map((o) => (
                      <p key={o} className="opozorilo">
                        {o}
                      </p>
                    ))}
                    <TabelaPartnerjev tabela={tabela} naCelico={setCelica} izbrana={celica?.vrstica ?? null} />
                  </section>
                ))
              )
            ) : (
            odgovor.tabele.map((tabela) => (
              <section key={tabela.naslov} className="tabela-odsek">
                {odgovor.tabele.length > 1 && <h2>{tabela.naslov}</h2>}
                <p className="podnaslov">{tabela.pojasnilo}</p>
                {tabela.opozorila.map((o) => (
                  <p key={o} className="opozorilo">
                    {o}
                  </p>
                ))}
                <TabelaPorocila
                  porocilo={tabela}
                  globina={globinaZa(tabela)}
                  naCelico={setCelica}
                  izbrana={celica?.vrstica ?? null}
                />
              </section>
            ))
            )}
          </>
        )}
      </div>

      {celica !== null && (
        <PanelRazclenitve
          podjetje={id}
          celica={celica}
          naZapri={() => {
            setCelica(null);
          }}
        />
      )}
    </main>
  );
}
